import React, { useState } from "react";
import DSInput from "@/componentsDS/Input";
import Icon from "@/componentsDS/Icon/Icon";
import { IconSize, IconStyle } from "@/componentsDS/Icon/Icon.type";
import parse from "html-react-parser";
import Dropdown from "@/componentsDS/Dropdown/Dropdown";
import Button from "@/componentsDS/Button/Button";
import { ButtonSize, ButtonType, ButtonVariant } from "@/componentsDS/Button/Button.type";
import Alert from "@/components/Alert/index";
import { AlertType } from "@/components/Alert/alert.type";
import useTermClause from "@/hooks/use-term-clause/index";
import CardConfigure from "@/componentsDS/Modal/CardConfigure";
import { useSelector } from "react-redux";
import merge from "lodash/merge";
import Toast from "@/components/Toast/index";
import PropTypes from 'prop-types';

const ModelTermForm = ({ setCreateModelTerm, termClause, termTemplate, isEdit }) => {
    
    const [updatedValues, setUpdatedValues] = useState(isEdit ? merge({}, ...Object.values(termClause.clausePayload)) : {});
    const [errorState, setErrorState] = useState({});
    const [termName, setTermName] = useState('')

    const { createTermClause, updateTermClause, toastList } = useTermClause();
    const profile = useSelector(state => state.auth.profile);

    const validateModelTerm = (sections) => {
        let errorState = {};
        let isError = false;
        sections.map(([key, value]) => {   
            const requiredFields = value.required;
            if(!requiredFields.every(item => {
                return updatedValues.hasOwnProperty(item) || value.properties[item].default
            })) {
                errorState[key] = 'There are incomplete fields. Please complete all fields to proceed.';
                isError = true
            }            
        })
        if(isError) {
            return setErrorState(errorState)
        }
        setErrorState({})
        return true
    }


    const onSubmitHandler = async() => {

        const modelTermSections = Object.entries(termTemplate.elementSchema.properties);
       
        const isValid = validateModelTerm(modelTermSections)
        if(!isValid) {
            return;
        }

        const clausePayload = {};
        
        modelTermSections.forEach(([key, value]) => {
            const keys = Object.keys(value.properties);
            const keyPayload = {}
            keys.forEach(fieldKey => {
                if (Object.hasOwn(updatedValues, fieldKey)) {
                    keyPayload[fieldKey] = updatedValues[fieldKey];
                } else {
                    keyPayload[fieldKey] = value.properties[fieldKey].default
                }
            });
            clausePayload[key] = keyPayload;
        })

        let payload = { clausePayload }

        let data;
        if (isEdit) {
			data = await updateTermClause(termClause.id, payload);
		} else {
			data = await createTermClause({
				...payload,
				clauseName: termName,
				templateId: termTemplate.templateId,
				orgId: profile?.organizationId,
			});
		}
		if (data) {
			setCreateModelTerm(false);
		}
    }


    const handleClick = (id, val) => {
        setUpdatedValues((prev) => ({ ...prev, [id]: val[0].id }));
    }

	const replacableContent = (domNode, schema) => {
		const properties = schema.properties;
		if (
			domNode.name === "span" &&
			domNode.attribs?.class?.includes("replaceable")
		) {
			const id = domNode.attribs.id;
			const option = properties[id];
			const checkboxOptions = option?.["x-uioptions"]?.options;
			const dropdownOptions = (checkboxOptions?.length > 0 ? checkboxOptions : option?.values || []).map(item => ({
				id: item.value ?? item,
				label: item.label ?? item,
				category: option?.name,
			}));
			const originalText = domNode.children[0]?.data;

			return (
				<div className="replaceable">
					<Dropdown
						setWidth={320}
						options={dropdownOptions}
						portal={true}
						value={[{ id: updatedValues[id] ?? option.default }]}
						onAdd={(val) => handleClick(id, val)}
						trigger={
							<Button
								text={
									updatedValues[id] ||
									option.default ||
									originalText
								}
								type={ButtonType.LINK}
								variant={ButtonVariant.STANDARD}
							/>
						}
					/>
				</div>
			);
		}
	};

	const renderSections = () => {
		return Object.entries(termTemplate.elementSchema.properties).map(
			([key, value]) => {
                const innerHtml = termTemplate.templateText[key].replaceAll(String.raw`\n`, '\n').replaceAll(String.raw`\"`, '"');
				return (
					<CardConfigure key={key} addHeaderClass={"account-settings__model-term__card-configure"}>
						<CardConfigure.Header>
							<CardConfigure.Header.Title>
								<div>
									<div className="account-settings__create-modal-term__section__name">{value.name}</div>
									<div className="account-settings__create-modal-term__section__info">
										<Icon
											name="information-circle"
											size={IconSize.MEDIUM}
											style={IconStyle.SOLID}
										/>
										<p>{value.helperText}</p>
									</div>
									{errorState[key] && <Alert icon = {<Icon name="information-circle" size={IconSize.MEDIUM} style={IconStyle.SOLID}/>} type={AlertType.ERROR} message={errorState[key]}/>}
								</div>
							</CardConfigure.Header.Title>
						</CardConfigure.Header>
						<CardConfigure.Body>
							<CardConfigure.Body.Col>
								<div>{parse(innerHtml, { replace : (domNode) => replacableContent(domNode, termTemplate.elementSchema.properties[key])})}</div>
							</CardConfigure.Body.Col>
						</CardConfigure.Body>
					</CardConfigure>
				);
			}
		);
	};

	return (
		<>

			<div className="account-settings__create-modal_term">
				<div className="account-settings__create-modal_term__title">
					Fill eBL Template
				</div>
				<hr />
				<div className="account-settings__create-modal_term__subtitle">
					Select and customise the legal terms to be included in your
					electronic bill of lading.
				</div>
				{!isEdit && (
					<DSInput
						name="termName"
						label="Template Name"
						placeholder="Enter Text"
						required={true}
						id="model-term-input"
						fullWidth={true}
						// pitstop's DSInput (componentsDS/Input/index.jsx) fires onChange with the
						// native event (its own handler is clobbered by the inputprops spread), NOT the
						// string value like admin-ui's Input. Extract the value so termName stays a
						// string — otherwise termName.trim() (Submit's disabled) crashes the page.
						onChange={(e) => setTermName(e?.target?.value ?? '')}
					/>
				)}
				<div>{renderSections()}</div>
			</div>

			<div className="DS__layout__footer">
				<div className="layout-default__sticky-actions">
					<div className="layout-default__sticky-actions__left">
						<Button
							text="Cancel"
							variant={ButtonVariant.STANDARD}
							type={ButtonType.GHOST}
							onClick={() => {
								setCreateModelTerm(false);
							}}
						/>
					</div>
					<div className="layout-default__sticky-actions__right layout-default--flex">
						<Button
							text="Submit"
							variant={ButtonVariant.STANDARD}
							type={ButtonType.SOLID}
							size={ButtonSize.DEFAULT}
							onClick={onSubmitHandler}
                            disabled={!isEdit && !termName.trim()}
							className="account-settings__modal_term-action"
						/>
					</div>
				</div>
			</div>
			<Toast toastList={toastList} />
		</>
	);
};

export default ModelTermForm;

ModelTermForm.propTypes = {
	setCreateModelTerm: PropTypes.func,
	termClause: PropTypes.object,
	termTemplate: PropTypes.object,
	isEdit: PropTypes.bool,
}